import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import useSEO from "@/hooks/useSEO";
import { Reveal } from "@/components/ui-lux";
import SpeakingForm from "@/components/SpeakingForm";

const Paras = ({ text, className = "lead mt-6" }) => (text || "").split("\n\n").filter(Boolean).map((p, i) => <p key={i} className={className}>{p}</p>);

const Label = ({ children, dark }) => (
  <p className={`font-accent text-xs uppercase tracking-[0.12em] mb-6 flex items-center gap-2 ${dark ? "text-black" : "text-slate-200"}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${dark ? "bg-black" : "bg-[#D4AF37]"}`} />{children}
  </p>
);

export default function About() {
  const { settings } = useSite();
  const a = settings?.about || {};
  useSEO({ title: a.title, description: a.tagline, jsonLd: { "@context": "https://schema.org", "@type": "Person", name: "Todd Coconato", jobTitle: "Founder, The Transformed Mind Experience" } });
  return (
    <div data-testid="about-page">
      <section className="pt-[116px] px-3 sm:px-6 lg:px-[8vw]">
        <div className="relative rounded-2xl overflow-hidden min-h-[600px] flex items-end">
          <img src="/images/todd-stadium.jpg" alt="Todd Coconato speaking" className="absolute inset-0 w-full h-full object-cover object-[25%_center]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C55] via-[#0A1C55]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/20 to-transparent hidden lg:block" />
          <div className="absolute inset-0 bg-black/40 lg:hidden" />
          <Reveal className="relative p-6 sm:p-12 lg:ml-auto lg:max-w-[52%]">
            <span className="eyebrow">About</span>
            <h1 className="h1" data-testid="about-title">{a.title}</h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-100 font-medium" data-testid="about-tagline">{a.tagline}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="container-x grid lg:grid-cols-[1fr_1.2fr] gap-14 items-center">
          <Reveal><img src={a.image || "/images/todd-4739.jpg"} alt="Pastor Todd Coconato" className="w-full aspect-[4/5] object-cover rounded-2xl" /></Reveal>
          <Reveal delay={0.15} className="[&>p:first-child]:mt-0" data-testid="about-intro"><Paras text={a.intro} /></Reveal>
        </div>
      </section>

      <section className="bg-white text-black py-24">
        <div className="container-x">
          <Reveal>
            <Label dark>What we believe</Label>
            <p className="font-serif text-4xl sm:text-6xl font-semibold tracking-[-0.045em] leading-[1.02] max-w-5xl" data-testid="about-belief">{a.belief}</p>
            <p className="mt-10 text-lg text-slate-700 leading-relaxed max-w-3xl">{a.problem}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="container-x">
          <Reveal><Label>The journey</Label><p className="h2 !text-3xl sm:!text-4xl max-w-4xl">{a.equip_intro}</p></Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden" data-testid="about-pillars">
            {(a.pillars || []).map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05} className="bg-black p-8 sm:p-10 h-full">
                <p className="font-accent text-xs text-gold-light">0{i + 1}</p>
                <h3 className="font-serif text-2xl font-semibold tracking-tight text-white mt-4">{p.title}</h3>
                <p className="text-slate-400 mt-3 leading-relaxed">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#05070F]">
        <div className="container-x grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
          <Reveal>
            <Label>Founder</Label>
            <h2 className="h2">{a.lifestyle_title}</h2>
            <Paras text={a.lifestyle_body} />
          </Reveal>
          <Reveal delay={0.2}><img src="/images/todd-4707.jpg" alt="Todd Coconato" className="w-full aspect-square object-cover rounded-2xl" /></Reveal>
        </div>
      </section>

      <section className="bg-white text-black py-24">
        <div className="container-x">
          <Reveal>
            <Label dark>Our mission</Label>
            <p className="font-serif text-3xl sm:text-5xl font-semibold tracking-[-0.04em] leading-[1.05] max-w-5xl" data-testid="about-mission">{a.mission}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="container-x max-w-4xl">
          <Reveal>
            <Paras text={a.closing} className="text-xl sm:text-2xl text-slate-200 leading-relaxed mt-6 first:mt-0" />
            <blockquote className="mt-12 border-l-2 border-[#D4AF37] pl-6 font-serif text-2xl sm:text-3xl italic text-white" data-testid="about-verse">{a.verse}</blockquote>
          </Reveal>
        </div>
      </section>

      <section id="invite" className="py-24 bg-[#05070F] scroll-mt-28" data-testid="speaking-section">
        <div className="container-x grid lg:grid-cols-[1fr_1.6fr] gap-14">
          <Reveal>
            <Label>Speaking</Label>
            <h2 className="h2">Invite Todd to Speak.</h2>
            <p className="lead mt-6">Bring the message of The Transformed Mind Experience to your church, conference, ministry or organization. Share a few details and Todd's team will be in touch.</p>
          </Reveal>
          <Reveal delay={0.15}><SpeakingForm /></Reveal>
        </div>
      </section>

      <section className="px-3 sm:px-6 lg:px-[8vw]">
        <div className="relative rounded-2xl overflow-hidden">
          <img src="/images/todd-4691.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1C55] via-[#0A1C55]/90 to-[#0A1C55]/40" />
          <Reveal className="relative p-8 sm:p-14 lg:p-20 max-w-3xl">
            <h2 className="h2" data-testid="about-final-headline">{a.final_headline}</h2>
            <Paras text={a.final_body} className="lead mt-5 !text-slate-100" />
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" className="btn-gold" data-testid="about-start-btn">Start Your Transformation</Link>
              <Link to="/videos" className="btn-outline" data-testid="about-teachings-btn">Free Discipleship Videos</Link>
              <a href="#invite" className="btn-ghost-gold" data-testid="about-invite-btn">Invite Todd to Speak</a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
